import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v7'
import { MantineProvider } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { MedplumProvider } from '@medplum/react-hooks'
import { SeenMedplumClient } from '@seen/core/client'
import {
  AdminTools,
  App,
  CarePlansPage,
  CollaborativeEditorRegistryProvider,
  ConfigContext,
  CurrentParticipantProvider,
  DailyMealQueuePage,
  DeskRoutePage,
  DesksPage,
  DocumentInboxPage,
  DraftsPage,
  getPatientRoutes,
  InboxDonePage,
  InboxPage,
  installDemoModeIfStored,
  KnockProvider,
  LiveblocksCopilotContext,
  LiveblocksProvider,
  Loader,
  MorePage,
  NoteLabelsPage,
  NotePage,
  NotesPage,
  ObservationsPage,
  OrderLabelsPage,
  OrdersPage,
  OrganizationConditionsPage,
  ParticipantLabelsPage,
  ParticipantsPage,
  PatientShell,
  PhoneCallPage,
  PhoneCallsPage,
  Placeholder,
  PlatingDetailPage,
  QueryProvider,
  ResourceBreadcrumb,
  refreshMedplumIfExpired,
  reportBundleRetry,
  reportWarning,
  SDRDisplayPage,
  SDRsPage,
  SearchPage,
  SeenApiContextProvider,
  SeenMantineTheme,
  ServingDetailPage,
  TaskLabelsPage,
  TaskPage,
  TasksPage,
  TemplatePage,
  TemplatesPage,
  usePortalStore,
} from '@seen/react'
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools'
import {
  type JSX,
  lazy,
  StrictMode,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Outlet, type RouteObject, redirect } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import { createPortalAiClient, createSeenAiApi } from '#src/api/ai.ts'
import { createPortalNpiApi } from '#src/api/npiLookup.ts'
import { BootFallback } from '#src/BootFallback.tsx'
import { type BootStatus, createBootWatch } from '#src/bootGate.ts'
import { McpAccountMenuItem } from '#src/components/McpAccountMenuItem.tsx'
import {
  APP_COMMIT_SHA,
  APP_VERSION,
  ELATION_URL,
  IS_DEV,
  IS_STAGING,
  KNOCK_API_PUBLIC_KEY,
  KNOCK_FEED_CHANNEL_ID,
  KNOCK_GUIDE_CHANNEL_ID,
  LIVEBLOCKS_COPILOT_ID,
  MEDPLUM_BASE_URL,
  MEDPLUM_GOOGLE_CLIENT_ID,
  MEDPLUM_PROJECT_ID,
  SEEN_API_URL,
} from '#src/config.ts'
import { FeatureFlagBootstrap } from '#src/FeatureFlagBootstrap.tsx'
import { MCP_OAUTH_CALLBACK_PATH } from '#src/mcp/contract.ts'
import { AiSkillPage } from '#src/pages/ai-skills/AiSkillPage.tsx'
import { AiSkillsPage } from '#src/pages/ai-skills/AiSkillsPage.tsx'
import { CareTeamsPage } from '#src/pages/care-teams/CareTeamsPage.tsx'
import { CcdaPage } from '#src/pages/ccda/CcdaPage.tsx'
import { CcdaViewerPage } from '#src/pages/ccda/CcdaViewerPage.tsx'
import { DocumentsPage } from '#src/pages/documents/DocumentsPage.tsx'
import { IncidentsPage } from '#src/pages/incidents/IncidentsPage.tsx'
import {
  LegacyIntakeRedirectPage,
  LegacyLeadRedirectPage,
  LegacyNewLeadRedirectPage,
} from '#src/pages/LegacyLeadRedirectPage.tsx'
import { McpCallbackPage } from '#src/pages/McpCallbackPage.tsx'
import { OnboardingPage } from '#src/pages/OnboardingPage.tsx'
import { OrderRedirectPage } from '#src/pages/OrderRedirectPage.tsx'
import { OrganizationDetailPage } from '#src/pages/organizations/OrganizationDetailPage.tsx'
import { OrganizationsPage } from '#src/pages/organizations/OrganizationsPage.tsx'
import { PractitionerDetailPage } from '#src/pages/PractitionerDetailPage.tsx'
import { PractitionerManagerPage } from '#src/pages/PractitionerManagerPage.tsx'
import { QuestionnaireToZodPage } from '#src/pages/QuestionnaireToZodPage.tsx'
import { RETURN_URL_KEY, SignInPage } from '#src/pages/SignInPage.tsx'

const search = new URLSearchParams(window.location.search)
const traceIdFromUrl = search.get('DEBUG_TRACE_ID')
if (traceIdFromUrl) {
  sessionStorage.setItem('DEBUG_TRACE_ID', traceIdFromUrl)
}
const traceId =
  traceIdFromUrl ?? sessionStorage.getItem('DEBUG_TRACE_ID') ?? undefined

installDemoModeIfStored()

const medplum = new SeenMedplumClient({
  traceId,
  onBundleRetry: reportBundleRetry,
  baseUrl: MEDPLUM_BASE_URL,
  onUnauthenticated: () => {
    const authContext = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      online: navigator.onLine,
      memory_token_exists: !!medplum.getAccessToken(),
      base_url: MEDPLUM_BASE_URL,
      activeLogin_exists: !!localStorage.getItem('activeLogin'),
      logins_exists: !!localStorage.getItem('logins'),
    }
    reportWarning(
      `Authentication lost - Context: ${JSON.stringify(authContext)}`
    )

    const currentPath =
      window.location.pathname + window.location.search + window.location.hash
    if (!currentPath.startsWith('/signin')) {
      localStorage.setItem(
        RETURN_URL_KEY,
        JSON.stringify({ url: currentPath, ts: Date.now() })
      )
      window.location.href = '/signin'
    }
  },
  cacheTime: 1000,
  // disable auto batching in dev mode to make debugging network requests easier
  // autoBatchTime: IS_DEV ? 0 : 100,
  //
  // There is a general concensus that batching is good, especially for optimizing n+1 requests
  // However, there are times when we want to opt-out of batching for 'high-priority' requests
  // https://seenhealth.slack.com/archives/C07V6Q9BGS2/p1736360343052899
  // https://github.com/medplum/medplum/pull/5751
  // TODO: re-add batching after the above issues are resolved and we can opt-out of batching
  // for 'high-priority' requests
  autoBatchTime: 0,
})

declare global {
  interface Window {
    __TEST_MEDPLUM_SIGN_OUT__?: () => Promise<void>
  }
}

// Expose signOut for testing auth loss: await __TEST_MEDPLUM_SIGN_OUT__()
if (typeof window !== 'undefined') {
  window.__TEST_MEDPLUM_SIGN_OUT__ = () => medplum.signOut()
}

function ensureIdParam(id: string | undefined): string {
  if (!id) {
    throw new Error('No id')
  }
  return id
}

type TypedHandleRouteObject = Omit<RouteObject, 'handle' | 'children'> & {
  handle?: {
    crumb?: (matchData: any) => JSX.Element[] | JSX.Element
  }
  children?: TypedHandleRouteObject[]
}

// Lazy-loaded so FullCalendar + its 8 plugins ship in their own chunk
// rather than the main bundle (~113 KB gzipped off initial load).
const CalendarPage = lazy(() =>
  import('#src/pages/calendar/Calendar.tsx').then((m) => ({
    default: m.CalendarPage,
  }))
)

// Lazy-loaded so the lane grid + scheduling engine client stay out of the
// initial bundle (unadvertised: URL-only, no nav entry).
const SchedulePage = lazy(() =>
  import('#src/pages/schedule/SchedulePage.tsx').then((m) => ({
    default: m.SchedulePage,
  }))
)

// Lazy-loaded so the AI chat widget, AI SDK, and markdown/Shiki stack ship in
// their own chunk that loads only when a user opens the assistant, instead of
// the portal's initial bundle. The shell wraps {assistantPanel} in Suspense.
const AIAssistantPanel = lazy(() =>
  import('#src/components/app/AIAssistantPanel.tsx').then((m) => ({
    default: m.AIAssistantPanel,
  }))
)

// The internal /dev + design-system pages live behind the `dev` route and are
// never reached in clinical workflows, so they're lazy-loaded into their own
// chunk via a portal-local re-export module rather than the initial bundle.
// The parent `dev` route renders DevSection, whose Suspense covers them all.
const ConditionsAuditPage = lazy(() =>
  import('#src/pages/dev/devPages.ts').then((m) => ({
    default: m.ConditionsAuditPage,
  }))
)
const ConditionsComboboxDevPage = lazy(() =>
  import('#src/pages/dev/devPages.ts').then((m) => ({
    default: m.ConditionsComboboxDevPage,
  }))
)
const DesignColorsPage = lazy(() =>
  import('#src/pages/dev/devPages.ts').then((m) => ({
    default: m.DesignColorsPage,
  }))
)
const DesignSystemLayout = lazy(() =>
  import('#src/pages/dev/devPages.ts').then((m) => ({
    default: m.DesignSystemLayout,
  }))
)
const DesignTypographyPage = lazy(() =>
  import('#src/pages/dev/devPages.ts').then((m) => ({
    default: m.DesignTypographyPage,
  }))
)
const DevPage = lazy(() =>
  import('#src/pages/dev/devPages.ts').then((m) => ({ default: m.DevPage }))
)
const MedReqLinkAuditPage = lazy(() =>
  import('#src/pages/dev/devPages.ts').then((m) => ({
    default: m.MedReqLinkAuditPage,
  }))
)
const PharmacySimulatorPage = lazy(() =>
  import('#src/pages/dev/devPages.ts').then((m) => ({
    default: m.PharmacySimulatorPage,
  }))
)
const VisitNoteLinkAuditPage = lazy(() =>
  import('#src/pages/dev/devPages.ts').then((m) => ({
    default: m.VisitNoteLinkAuditPage,
  }))
)

// Single Suspense boundary for every lazy /dev page.
function DevSection(): JSX.Element {
  return (
    <Suspense fallback={<Loader />}>
      <Outlet />
    </Suspense>
  )
}

/**
 * How long the boot may wait on the Medplum auth/profile call before we offer a
 * reload instead of an indefinite spinner. The call still completes (and the app
 * proceeds) on its own if it resolves after this — see {@link createBootWatch}.
 */
const BOOT_WATCHDOG_MS = 15_000

export function Root(): JSX.Element {
  const showReactQueryDevtools = usePortalStore(
    (state) => state.showReactQueryDevtools
  )
  const toggleReactQueryDevtools = usePortalStore(
    (state) => state.toggleReactQueryDevtools
  )

  const [bootStatus, setBootStatus] = useState<BootStatus>('loading')

  useEffect(
    () =>
      // The whole app is gated on this single Medplum call, which has no
      // built-in timeout. Guard it so a stalled or failed request surfaces a
      // retry instead of spinning forever. Returning the cancel fn as cleanup
      // keeps this correct under StrictMode's double-invoke (the call returns
      // the same in-flight promise, so re-running is harmless).
      createBootWatch(medplum.getProfileAsync(), BOOT_WATCHDOG_MS, {
        onStatus: setBootStatus,
        onStall: (reason) =>
          reportWarning(`Orbit boot stalled before profile ready: ${reason}`),
      }),
    []
  )

  // Monitor Medplum's localStorage for auth debugging
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === 'activeLogin' || e.key === 'logins') {
        if (e.oldValue && !e.newValue) {
          reportWarning(
            `Medplum ${e.key} cleared from storage - URL: ${e.url || 'unknown'}`
          )
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  /* Refresh tokens when user returns to an idle tab */
  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && medplum.getActiveLogin()) {
        void refreshMedplumIfExpired(medplum)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const patientRoutes = useMemo(getPatientRoutes, [])
  const aiClient = useMemo(() => createPortalAiClient(medplum), [])
  const seenAiApi = useMemo(() => createSeenAiApi(aiClient), [aiClient])
  const seenNpiApi = useMemo(() => createPortalNpiApi(aiClient), [aiClient])
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/',
          element: (
            <App
              isStaging={IS_STAGING}
              medplumProjectId={MEDPLUM_PROJECT_ID}
              medplumGoogleClientId={MEDPLUM_GOOGLE_CLIENT_ID}
              disableEmailAuth={false}
              appVersion={APP_VERSION}
              appCommitSha={APP_COMMIT_SHA}
              assistantPanel={<AIAssistantPanel />}
              accountMenuItems={<McpAccountMenuItem />}
            ></App>
          ),
          children: [
            { index: true, element: <InboxPage /> },
            {
              path: MCP_OAUTH_CALLBACK_PATH.slice(1),
              element: <McpCallbackPage />,
            },
            { path: 'inbox/done', element: <InboxDonePage /> },
            { path: 'document/inbox', element: <DocumentInboxPage /> },
            {
              path: 'fax/inbox',
              loader: ({ request }) => {
                const url = new URL(request.url)
                return redirect(`/document/inbox${url.search}${url.hash}`)
              },
            },
            { path: 'search', element: <SearchPage /> },
            { path: 'Patient', element: <ParticipantsPage /> },
            { path: 'onboarding', element: <OnboardingPage /> },
            {
              path: 'calendar',
              element: (
                <Suspense fallback={<Loader />}>
                  <CalendarPage />
                </Suspense>
              ),
            },
            {
              path: 'schedule',
              element: (
                <Suspense fallback={<Loader />}>
                  <SchedulePage />
                </Suspense>
              ),
            },
            { path: 'meal-queue', element: <DailyMealQueuePage /> },
            { path: 'meal-plating-detail', element: <PlatingDetailPage /> },
            { path: 'meal-serving-detail', element: <ServingDetailPage /> },
            {
              path: 'organizations',
              children: [
                { index: true, element: <OrganizationsPage /> },
                {
                  path: ':organizationId',
                  element: <OrganizationDetailPage />,
                },
              ],
            },
            { path: 'documents', element: <DocumentsPage /> },
            {
              path: 'notes',
              children: [
                { index: true, element: <NotesPage /> },
                { path: ':compositionId', element: <NotePage /> },
              ],
            },
            { path: 'incidents', element: <IncidentsPage /> },
            {
              path: 'ccda',
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<Loader />}>
                      <CcdaPage />
                    </Suspense>
                  ),
                },
                {
                  path: ':patientId/export/:communicationRequestId',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <CcdaViewerPage />
                    </Suspense>
                  ),
                },
              ],
            },
            { path: 'drafts', element: <DraftsPage /> },
            { path: 'care-plans', element: <CarePlansPage /> },
            {
              path: 'tasks',
              children: [
                { index: true, element: <TasksPage /> },
                {
                  path: ':taskId',
                  element: <TaskPage />,
                  loader: ({ params }: { params: { taskId?: string } }) => ({
                    taskId: ensureIdParam(params.taskId),
                  }),
                  handle: {
                    crumb: ({ taskId }: { taskId: string }) => (
                      <ResourceBreadcrumb
                        to={`tasks/${taskId}`}
                        resourceType="Task"
                        resourceId={taskId}
                      />
                    ),
                  },
                },
              ],
            },
            {
              path: 'sdr',
              children: [
                { index: true, element: <SDRsPage /> },
                {
                  path: ':taskId',
                  element: <SDRDisplayPage />,
                  loader: ({ params }: { params: { taskId?: string } }) => ({
                    taskId: ensureIdParam(params.taskId),
                  }),
                  handle: {
                    crumb: ({ taskId }: { taskId: string }) => (
                      <ResourceBreadcrumb
                        to={`sdr/${taskId}`}
                        resourceType="Task"
                        resourceId={taskId}
                      />
                    ),
                  },
                },
              ],
            },
            {
              path: 'orders',
              children: [
                { index: true, element: <OrdersPage /> },
                { path: 'new', element: <OrdersPage /> },
                { path: ':orderId', element: <OrderRedirectPage /> },
              ],
            },
            { path: 'settings', element: <Placeholder /> },
            {
              path: 'intake',
              element: <LegacyIntakeRedirectPage />,
            },
            {
              path: 'intake/new-lead',
              element: <LegacyNewLeadRedirectPage />,
            },
            {
              path: '/intake/lead/:patientId',
              element: <LegacyLeadRedirectPage />,
            },
            {
              path: 'admin-tools',
              children: [
                { index: true, element: <AdminTools /> },
                {
                  path: 'questionnaire-to-zod',
                  element: <QuestionnaireToZodPage />,
                },
              ],
            },
            {
              path: 'dev',
              element: <DevSection />,
              children: [
                { index: true, element: <DevPage /> },
                {
                  path: 'med-req-link-audit',
                  element: <MedReqLinkAuditPage />,
                },
                {
                  path: 'visit-note-link-audit',
                  element: <VisitNoteLinkAuditPage />,
                },
                {
                  path: 'conditions-audit',
                  element: <ConditionsAuditPage />,
                },
                {
                  path: 'conditions-combobox',
                  element: <ConditionsComboboxDevPage />,
                },
                {
                  path: 'pharmacy-simulator',
                  element: <PharmacySimulatorPage />,
                },
                {
                  path: 'design',
                  element: <DesignSystemLayout />,
                  children: [
                    {
                      path: 'colors',
                      element: <DesignColorsPage />,
                    },
                    {
                      path: 'typography',
                      element: <DesignTypographyPage />,
                    },
                  ],
                },
              ],
            },
            {
              path: 'Patient/:patientId',
              element: (
                <CurrentParticipantProvider>
                  <PatientShell />
                </CurrentParticipantProvider>
              ),
              loader: ({ params }: { params: { patientId?: string } }) => ({
                patientId: ensureIdParam(params.patientId),
              }),
              handle: {
                crumb: ({ patientId }: { patientId: string }) => (
                  <ResourceBreadcrumb
                    to={`/Patient/${patientId}`}
                    resourceType="Patient"
                    resourceId={patientId}
                  />
                ),
              },
              children: patientRoutes,
            },
            {
              path: 'desks',
              children: [
                { index: true, element: <DesksPage /> },
                { path: ':deskId', element: <DeskRoutePage /> },
              ],
            },
            {
              path: 'templates',
              children: [
                { index: true, element: <TemplatesPage /> },
                { path: ':templateId', element: <TemplatePage /> },
              ],
            },
            {
              path: 'settings',
              children: [
                {
                  path: 'skills',
                  children: [
                    { index: true, element: <AiSkillsPage /> },
                    { path: ':skillId', element: <AiSkillPage /> },
                  ],
                },
                { path: 'task-labels', element: <TaskLabelsPage /> },
                {
                  path: 'observations',
                  element: <ObservationsPage />,
                },
                {
                  path: 'participant-labels',
                  element: <ParticipantLabelsPage />,
                },
                {
                  path: 'note-labels',
                  element: <NoteLabelsPage />,
                },
                {
                  path: 'order-labels',
                  element: <OrderLabelsPage />,
                },
                {
                  path: 'organization-conditions',
                  element: <OrganizationConditionsPage />,
                },
              ],
            },
            {
              path: 'calls',
              children: [
                { index: true, element: <PhoneCallsPage /> },
                { path: ':communicationId', element: <PhoneCallPage /> },
              ],
            },
            { path: '/care-teams', element: <CareTeamsPage /> },
            { path: '/practitioners', element: <PractitionerManagerPage /> },
            {
              path: '/practitioners/:practitionerId',
              element: <PractitionerDetailPage />,
            },
            {
              path: 'more',
              element: <MorePage />,
            },
          ],
        },
        { path: '/signin', element: <SignInPage /> },
      ] satisfies TypedHandleRouteObject[]),
    [patientRoutes]
  )

  function navigate(path: string): Promise<void> {
    return router.navigate(path)
  }

  if (bootStatus === 'stalled') {
    return <BootFallback onReload={() => window.location.reload()} />
  }

  if (bootStatus !== 'ready') {
    return <Loader />
  }

  return (
    <StrictMode>
      <ConfigContext.Provider value={{ elationBaseUrl: ELATION_URL }}>
        <MedplumProvider medplum={medplum} navigate={navigate}>
          <QueryProvider
            defaultQueryOptions={{
              staleTime: IS_DEV ? 0 : 1000 * 60 * 1, // off in dev mode, 1 minute in prod
            }}
          >
            <FeatureFlagBootstrap>
              <SeenApiContextProvider
                baseURL={SEEN_API_URL}
                medplum={medplum}
                appVersion={APP_VERSION}
                appCommitSha={APP_COMMIT_SHA}
                // The portal serves the same-origin AI BFF; injecting the adapter is
                // what turns AI features on for this app.
                ai={seenAiApi}
                npi={seenNpiApi}
              >
                <LiveblocksProvider>
                  <LiveblocksCopilotContext.Provider
                    value={LIVEBLOCKS_COPILOT_ID}
                  >
                    <CollaborativeEditorRegistryProvider>
                      <KnockProvider
                        knockApiKey={KNOCK_API_PUBLIC_KEY}
                        knockFeedChannelId={KNOCK_FEED_CHANNEL_ID}
                        knockGuideChannelId={KNOCK_GUIDE_CHANNEL_ID}
                      >
                        <MantineProvider theme={SeenMantineTheme}>
                          <ModalsProvider>
                            <RouterProvider router={router} />
                          </ModalsProvider>
                        </MantineProvider>
                      </KnockProvider>
                    </CollaborativeEditorRegistryProvider>
                  </LiveblocksCopilotContext.Provider>
                </LiveblocksProvider>
              </SeenApiContextProvider>
            </FeatureFlagBootstrap>
            {showReactQueryDevtools && (
              <ReactQueryDevtoolsPanel
                style={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}
                onClose={toggleReactQueryDevtools}
              />
            )}
          </QueryProvider>
        </MedplumProvider>
      </ConfigContext.Provider>
    </StrictMode>
  )
}
